import {
  DownloadOutlined,
  EyeOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  App,
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { getSalesForReportFilter } from '../../services/reportService';
import { User } from '../../types';
import { useDebounce } from '../../utils/hooks';

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;
const { RangePicker } = DatePicker;

const LaporanPage = () => {
  const [leads, setLeads] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    start_date: '',
    end_date: '',
    property_area_id: '',
    tahapanFU: '',
    sales_user_id: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [propertyAreas, setPropertyAreas] = useState<any[]>([]);
  const [leadStatuses, setLeadStatuses] = useState<any[]>([]);
  const [salesUsers, setSalesUsers] = useState<User[]>([]);
  const { hasPermission } = useAuth();
  const { message } = App.useApp();
  const navigate = useNavigate();

  const shouldShowPropertyAreaFilter = hasPermission('lead.property');
  const shouldShowSalesUserFilter = hasPermission('lead.assign');

  const { current: currentPage, pageSize } = pagination;

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { sales_user_id, ...restFilters } = filters;
      const params: { [key: string]: any } = {
        page: currentPage,
        limit: pageSize,
        ...restFilters,
        sales_user: sales_user_id, // Correctly map to 'sales_user'
      };

      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await api.get('leads', { params });
      const { data, meta } = response.data;

      setLeads(data);
      setPagination(prev => ({
        ...prev,
        total: meta.total,
      }));
    } catch (error: any) {
      console.error('Failed to fetch leads:', error);
      message.error('Gagal memuat data laporan leads');
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, pageSize, message]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const leadStatusesRes = await api.get('lead-status?is_active=true');
        setLeadStatuses(leadStatusesRes.data.data || []);

        if (shouldShowPropertyAreaFilter) {
          const propertyAreasRes = await api.get('property-areas/role');
          setPropertyAreas((propertyAreasRes.data.data || []).filter((item: any) => item.is_active));
        }

        const salesUsersRes = await getSalesForReportFilter();
        setSalesUsers(salesUsersRes.data || []);
      } catch (error) {
        console.error('Failed to fetch filter data:', error);
        message.error('Gagal memuat data untuk filter');
      }
    };

    fetchFilterData();
  }, [shouldShowPropertyAreaFilter, message]);

  useEffect(() => {
    setFilters(prev => ({ ...prev, search: debouncedSearchTerm }));
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [debouncedSearchTerm]);

  const handleTableChange = (newPagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDateChange = (dates: any, dateStrings: [string, string]) => {
    setFilters(prev => ({
      ...prev,
      start_date: dateStrings[0] || '',
      end_date: dateStrings[1] || '',
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilters({
      search: '',
      start_date: '',
      end_date: '',
      property_area_id: '',
      tahapanFU: '',
      sales_user_id: '',
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Map frontend filter state to the parameter names expected by the report endpoint
      const { property_area_id, search, sales_user_id, ...otherFilters } = filters;

      const isPhoneNumber = /^\d+$/.test(search);

      const reportParams: { [key: string]: any } = {
        ...otherFilters,
        sales_user: sales_user_id, // Correctly map to 'sales_user'
        name: isPhoneNumber ? '' : search,
        phone: isPhoneNumber ? search : '',
      };

      if (property_area_id) {
        reportParams['property_area_ids'] = property_area_id;
      }

      // Remove any empty filters
      Object.keys(reportParams).forEach(key => {
        if (!reportParams[key]) delete reportParams[key];
      });

      const response = await api.get('/reports/lead-resume', { params: reportParams });
      const reportsData = response.data.data;

      if (!reportsData || reportsData.length === 0) {
        message.info('Tidak ada data untuk diexport berdasarkan filter yang dipilih.');
        return;
      }

      const wb = XLSX.utils.book_new();

      reportsData.forEach((report: any) => {
        const dataForSheet: any[][] = [];

        // --- Header ---
        dataForSheet.push([`Laporan Resume Leads - ${report.property_area_name}`]);
        dataForSheet.push([`Periode: ${report.report_periode_start} s/d ${report.report_periode_end}`]);
        dataForSheet.push([`Diexport pada: ${report.create_date}`]);
        dataForSheet.push([]); // Tambah baris kosong 1
        dataForSheet.push([]); // Tambah baris kosong 2

        // --- Table Headers ---
        const generalHeaders = [
          'No', 'Tgl Lead Dibuat', 'Tipe Sumber Lead', 'Sumber Lead', 'Nama Lengkap',
          'Lokasi', 'Email', 'No Telp', 'Whatsapp', 'Tipe Properti', 'Budget',
          'Properti Area', 'Sales Force', 'Status Ketertarikan'
        ];
        const fu1Headers = ['Tgl Follow Up', 'Action', 'Status', 'Kategori', 'Alasan Tidak Minat/Tidak Jadi Pesan'];
        const fu2Headers = ['Tgl Follow Up', 'Action', 'Status', 'Kategori', 'Rencana Visit', 'Alasan Tidak Minat/Tidak Jadi Pesan'];
        const fu3Headers = ['Tgl Follow Up', 'Action', 'Status', 'Kategori', 'Realisasi Visit', 'Alasan Tidak Minat/Tidak Jadi Pesan'];

        // Row 1 of Headers (Main Titles)
        const mainHeaderRow = [
          'Informasi Umum', ...Array(generalHeaders.length - 1).fill(''),
          '', // Spacer
          'Follow Up 1', ...Array(fu1Headers.length - 1).fill(''),
          '', // Spacer
          'Follow Up 2', ...Array(fu2Headers.length - 1).fill(''),
          '', // Spacer
          'Follow Up 3', ...Array(fu3Headers.length - 1).fill(''),
        ];
        dataForSheet.push(mainHeaderRow);

        // Row 2 of Headers (Sub-headers)
        const subHeaderRow = [
          ...generalHeaders,
          '', // Spacer
          ...fu1Headers,
          '', // Spacer
          ...fu2Headers,
          '', // Spacer
          ...fu3Headers
        ];
        dataForSheet.push(subHeaderRow);

        // --- Table Body ---
        report.leads.forEach((lead: any, index: number) => {
          const generalData = [
            lead.no,
            lead.created_at ? new Date(lead.created_at).toLocaleString('id-ID') : '-',
            lead.source_lead_type || '-',
            lead.souce_lead || '-', // Typo in DTO
            lead.full_name || '-',
            lead.address || '-',
            lead.email || '-',
            lead.phone || '-',
            lead.whatsapp || '-',
            lead.property_type || '-',
            lead.budget ? `Rp ${Number(lead.budget).toLocaleString('id-ID')}` : '-',
            lead.property_area || '-',
            lead.sales_force || '-',
            lead.status_interest || '-'
          ];

          const fu1Data = (fu: any) => {
            if (!fu) return Array(fu1Headers.length).fill('-');
            return [
              fu.date ? new Date(fu.date).toLocaleString('id-ID') : '-',
              fu.action || '-',
              fu.status || '-',
              fu.category || '-',
              fu.InterestStatusReason || '-'
            ];
          };
          const fu2Data = (fu: any) => {
            if (!fu) return Array(fu2Headers.length).fill('-');
            return [
              fu.date ? new Date(fu.date).toLocaleString('id-ID') : '-',
              fu.action || '-',
              fu.status || '-',
              fu.category || '-',
              fu.schedulle_date ? new Date(fu.schedulle_date).toLocaleDateString('id-ID') : '-',
              fu.InterestStatusReason || '-'
            ];
          };
          const fu3Data = (fu: any) => {
            if (!fu) return Array(fu3Headers.length).fill('-');
            return [
              fu.date ? new Date(fu.date).toLocaleString('id-ID') : '-',
              fu.action || '-',
              fu.status || '-',
              fu.category || '-',
              fu.Actual_Date ? new Date(fu.Actual_Date).toLocaleDateString('id-ID') : '-',
              fu.InterestStatusReason || '-'
            ];
          };

          const rowData = [
            ...generalData,
            '', // Spacer
            ...fu1Data(lead.follow_up_1),
            '', // Spacer
            ...fu2Data(lead.follow_up_2),
            '', // Spacer
            ...fu3Data(lead.follow_up_3),
          ];
          dataForSheet.push(rowData);
        });

        // --- Create Worksheet ---
        const worksheet = XLSX.utils.aoa_to_sheet(dataForSheet);

        // --- Apply Borders to All Cells in the data area ---
        const tableStartIndex = 5; // Headers start at row 6 (index 5)
        const range = XLSX.utils.decode_range(worksheet['!ref']!);
        for (let R = tableStartIndex; R <= range.e.r; ++R) {
          for (let C = range.s.c; C <= range.e.c; ++C) {
            const cell_address = { c: C, r: R };
            const cell_ref = XLSX.utils.encode_cell(cell_address);
            if (!worksheet[cell_ref]) {
              worksheet[cell_ref] = { t: 's', v: '' };
            }
            if (!worksheet[cell_ref].s) worksheet[cell_ref].s = {};
            worksheet[cell_ref].s.border = {
              top: { style: "thin", color: { auto: 1 } },
              bottom: { style: "thin", color: { auto: 1 } },
              left: { style: "thin", color: { auto: 1 } },
              right: { style: "thin", color: { auto: 1 } }
            };
          }
        }

        // --- Merging Cells for Main Titles ---
        const headerRowIndex = 5;
        const mergeInfo = { s: { r: headerRowIndex, c: 0 }, e: { r: headerRowIndex, c: generalHeaders.length - 1 } };

        const fu1StartCol = generalHeaders.length + 1;
        const fu1EndCol = fu1StartCol + fu1Headers.length - 1;
        const mergeFU1 = { s: { r: headerRowIndex, c: fu1StartCol }, e: { r: headerRowIndex, c: fu1EndCol } };

        const fu2StartCol = fu1EndCol + 2;
        const fu2EndCol = fu2StartCol + fu2Headers.length - 1;
        const mergeFU2 = { s: { r: headerRowIndex, c: fu2StartCol }, e: { r: headerRowIndex, c: fu2EndCol } };

        const fu3StartCol = fu2EndCol + 2;
        const fu3EndCol = fu3StartCol + fu3Headers.length - 1;
        const mergeFU3 = { s: { r: headerRowIndex, c: fu3StartCol }, e: { r: headerRowIndex, c: fu3EndCol } };

        if (!worksheet['!merges']) worksheet['!merges'] = [];
        worksheet['!merges'].push(mergeInfo, mergeFU1, mergeFU2, mergeFU3);

        // --- Adjust Column Widths ---
        const colWidths = [
          ...Array(generalHeaders.length).fill({ wch: 20 }),
          { wch: 3 }, // Spacer
          ...Array(fu1Headers.length).fill({ wch: 20 }),
          { wch: 3 }, // Spacer
          ...Array(fu2Headers.length).fill({ wch: 20 }),
          { wch: 3 }, // Spacer
          ...Array(fu3Headers.length).fill({ wch: 20 }),
        ];
        colWidths[0] = { wch: 5 }; // No
        colWidths[4] = { wch: 25 }; // Nama Lengkap
        colWidths[5] = { wch: 30 }; // Lokasi
        colWidths[6] = { wch: 25 }; // Email
        worksheet['!cols'] = colWidths;

        XLSX.utils.book_append_sheet(wb, worksheet, report.property_area_name.replace(/ /g, '_').substring(0, 31));
      });

      XLSX.writeFile(wb, `Laporan_Resume_Leads_${new Date().toISOString().slice(0, 10)}.xlsx`);
      message.success('Berhasil mengekspor data ke Excel.');

    } catch (error) {
      console.error('Failed to export data:', error);
      message.error('Gagal mengekspor data.');
    } finally {
      setExporting(false);
    }
  };

  const columns: any[] = [
    {
      title: 'Lead Info',
      key: 'lead_info',
      width: 300,
      align: 'left',
      render: (_: any, record: any) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <Avatar size="small" icon={<UserOutlined />} style={{ marginRight: 8 }} />
            <strong>{record.full_name}</strong>
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {record.email && (
              <div style={{ display: 'flex', alignItems: 'left', marginBottom: 2 }}>
                <MailOutlined style={{ marginRight: 4 }} />
                {record.email}
              </div>
            )}
            {record.phone && (
              <div style={{ display: 'flex', alignItems: 'left' }}>
                <PhoneOutlined style={{ marginRight: 4 }} />
                {record.phone}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Sumber',
      dataIndex: ['lead_source', 'name'],
      key: 'lead_source',
      width: 250,
      align: 'left',
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Property Area',
      dataIndex: ['property_area', 'name'],
      key: 'property_area',
      align: 'left',
      render: (text: string) => text || '-',
    },
    {
      title: 'Tahapan',
      dataIndex: ['lead_status', 'name'],
      key: 'tahapan',
      align: 'left',
      render: (status: string) => <Tag>{status?.toUpperCase() || 'N/A'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: ['lead_follow_up_status', 'name'],
      key: 'status',
      align: 'left',
      render: (status: string) => {
        if (!status) return <Tag>-</Tag>;
        const statusName = status.toLowerCase();
        let color = 'default';
        if (statusName === 'hot') color = 'blue';
        else if (statusName === 'rsv') color = 'success';
        else if (statusName === 'cold') color = 'red';
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Assigned To',
      dataIndex: ['assigned_to', 'full_name'],
      key: 'assigned_to',
      align: 'left',
      render: (text: string) => text || '-',
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 100,
      align: 'center',
      render: (_: any, record: any) => (
        <Tooltip title="Detail">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/leads/${record.id}`)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Laporan Leads
          </Title>
        </Col>
        <Col>
          <Space>
            {hasPermission('laporan.export') && (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExport}
                loading={exporting}
              >
                Export to Excel
              </Button>
            )}
            <Button onClick={resetFilters}>
              Reset Filter
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Cari nama atau no. telepon..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%' }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={handleDateChange}
              format="YYYY-MM-DD"
            />
          </Col>
          {shouldShowPropertyAreaFilter && (
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter Property Area"
                style={{ width: '100%' }}
                allowClear
                onChange={(value) => handleFilterChange('property_area_id', value)}
                value={filters.property_area_id || undefined}
              >
                {propertyAreas.map(area => (
                  <Option key={area.id} value={area.id}>{area.name}</Option>
                ))}
              </Select>
            </Col>
          )}
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter Tahapan Follow Up"
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => handleFilterChange('tahapanFU', value)}
              value={filters.tahapanFU || undefined}
            >
              {leadStatuses.map(status => (
                <Option key={status.id} value={status.id}>{status.name}</Option>
              ))}
            </Select>
          </Col>
          {shouldShowSalesUserFilter && (
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter Sales User"
                style={{ width: '100%' }}
                allowClear
                onChange={(value) => handleFilterChange('sales_user_id', value)}
                value={filters.sales_user_id || undefined}
              >
                {salesUsers.map(user => (
                  <Option key={user.id} value={user.id}>{user.full_name}</Option>
                ))}
              </Select>
            </Col>
          )}
        </Row>

        <Table
          columns={columns}
          dataSource={leads}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} leads`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default LaporanPage;

